/**
 * AAMI TIR34 Compliance Resource Page — B2B SEO magnet.
 *
 * Targets high-intent facility-manager / SPD / biomed queries:
 *   • "AAMI TIR34 water quality"
 *   • "TIR34 medical device reprocessing"
 *   • "AAMI TIR34 vs ST108"
 *   • "TIR34 utility water critical water"
 *
 * Structured as a TechnicalArticle so Google indexes it as authoritative
 * technical content targeted at biomedical engineers and SPD directors.
 */
import SEO, { technicalArticleSchema } from "@/components/SEO";
import SiteHeader from "@/components/SiteHeader";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { CheckCircle2, Droplets, FileText, ShieldCheck, ArrowRight, AlertTriangle, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";

const FAQS = [
  {
    q: "What is AAMI TIR34?",
    a: "AAMI TIR34 (Technical Information Report 34) — 'Water for the reprocessing of medical devices' — was AAMI's original guidance document defining water-quality expectations for sterile processing, endoscope reprocessing, and pharmacy compounding. First published in 2007 and updated in 2014, it introduced the utility-water / critical-water framework that hospitals still use today.",
  },
  {
    q: "Is TIR34 still valid, or has ST108 replaced it?",
    a: "AAMI ST108:2023 has superseded the water-quality content of TIR34. TIR34 remains useful as background/context, but ST108 is now the enforceable consensus standard cited by Joint Commission, DNV, and CMS. Facilities that reference TIR34 in their policies should update those policies to cite ST108.",
  },
  {
    q: "What are the key differences between TIR34 and ST108?",
    a: "TIR34 was a Technical Information Report (guidance, non-mandatory). ST108 is a formal ANSI-approved standard (mandatory language). ST108 adds explicit numeric limits for hardness, conductivity, TOC, and endotoxin; specifies testing frequency; and defines documentation and staff-competency requirements — none of which were codified in TIR34.",
  },
  {
    q: "Do hospitals still need to comply with TIR34?",
    a: "Not directly — surveyors now cite ST108. However, many hospitals still have TIR34-era water-treatment infrastructure. A ST108 gap assessment on TIR34-era systems typically reveals under-sized RO capacity, missing endotoxin monitoring, or gaps in documentation.",
  },
  {
    q: "How does Sterile Mate help facilities transition from TIR34 to ST108?",
    a: "Sterile Mate performs on-site TIR34→ST108 gap assessments across Texas, Louisiana, Alabama, and Mississippi. We provide upgrade quotes for central RO capacity, DI polishing loops, endotoxin/TOC/conductivity monitoring, and quarterly compliance documentation — all under one dispatch center in Houston.",
  },
];

const COMPARISON = [
  { field: "Document type", tir34: "Technical Information Report", st108: "ANSI-approved consensus standard" },
  { field: "Language", tir34: "Should / may (guidance)", st108: "Shall / must (mandatory)" },
  { field: "Water categories", tir34: "Utility / Critical (2 tiers)", st108: "Utility / Critical / Sterile-for-Injection (3 tiers)" },
  { field: "Endotoxin limit", tir34: "Recommended, not codified", st108: "≤ 10 EU/mL (Cat 2), ≤ 0.25 EU/mL (Cat 3)" },
  { field: "Testing frequency", tir34: "Advisory", st108: "Explicit minimums" },
  { field: "Staff competency", tir34: "Not addressed", st108: "Required documentation" },
];

export default function AamiTir34Page() {
  const canonical = "/resources/aami-tir34";
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      technicalArticleSchema({
        headline: "AAMI TIR34 Water Quality for Medical Device Reprocessing — 2024 Update & ST108 Migration Guide",
        description:
          "Complete AAMI TIR34 reference guide covering water-quality expectations for medical device reprocessing, plus a side-by-side comparison with the successor standard AAMI ST108:2023. Includes ST108 migration checklist and Gulf-region service coverage.",
        url: canonical,
        datePublished: "2026-01-20",
        dateModified: "2026-02-24",
        keywords: [
          "AAMI TIR34",
          "AAMI TIR34 water quality",
          "TIR34 medical device reprocessing",
          "AAMI TIR34 vs ST108",
          "TIR34 utility water",
          "TIR34 critical water",
          "sterile processing water standards",
          "endoscope reprocessing water quality",
        ],
      }),
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white" data-testid="aami-tir34-page">
      <SEO
        canonical={canonical}
        title="AAMI TIR34 Water Quality for Medical Device Reprocessing — Compliance Guide"
        description="Complete AAMI TIR34 reference guide plus side-by-side comparison with AAMI ST108:2023. Utility vs critical water, testing expectations, and Gulf-region compliance services."
        keywords={[
          "AAMI TIR34",
          "AAMI TIR34 water quality",
          "TIR34 medical device reprocessing",
          "AAMI TIR34 vs ST108",
          "sterile processing water standards",
        ]}
        schema={schema}
      />
      <SiteHeader />

      {/* HERO */}
      <section className="relative bg-gradient-to-br from-[#0a1a35] via-[#0b2447] to-[#0056A8] text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(34,211,238,0.18),transparent_60%)]" />
        <div className="relative max-w-6xl mx-auto px-6 py-20">
          <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-cyan-300 mb-4">
            Compliance Resource · TIR34 → ST108 Migration
          </p>
          <h1 className="text-4xl md:text-6xl font-black leading-[1.05] max-w-4xl">
            AAMI TIR34 Water Quality for Medical Device Reprocessing.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-white/85 max-w-3xl leading-relaxed">
            The 2007–2014 guidance document that defined hospital sterile-processing water for a
            generation — now superseded by <Link to="/resources/aami-st108" className="text-cyan-300 underline">AAMI ST108:2023</Link>. Use this
            page as a reference plus a side-by-side migration checklist across{" "}
            <strong>Texas, Louisiana, Alabama, and Mississippi.</strong>
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">
              <Link to="/resources/aami-st108">
                <BookOpen className="w-5 h-5 mr-2" />
                Read the ST108 Successor Guide
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
              <a href="mailto:info@sterilemate.com?subject=TIR34 to ST108 gap assessment">
                <FileText className="w-5 h-5 mr-2" />
                Request Gap Assessment
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-black text-slate-900 mb-2">TIR34 vs ST108 — Side-by-Side</h2>
        <p className="text-slate-600 mb-8">
          If your policies still reference TIR34, use this checklist to identify gaps against
          ST108 — the standard actively cited by Joint Commission surveyors since 2024.
        </p>
        <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-800">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Requirement</th>
                <th className="text-left px-4 py-3 font-bold">AAMI TIR34 (2007/2014)</th>
                <th className="text-left px-4 py-3 font-bold">AAMI ST108:2023</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((r) => (
                <tr key={r.field} className="border-t border-slate-200">
                  <td className="px-4 py-3 font-semibold text-slate-900">{r.field}</td>
                  <td className="px-4 py-3 text-slate-700">{r.tir34}</td>
                  <td className="px-4 py-3 text-slate-700 bg-emerald-50/50">{r.st108}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Sources: AAMI TIR34:2014/(R)2021 & ANSI/AAMI ST108:2023. Consult the full documents for complete requirements.
        </p>
      </section>

      {/* HOW WE HELP */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-black text-slate-900 mb-2">Migrating from TIR34 to ST108</h2>
          <p className="text-slate-600 mb-8 max-w-2xl">
            Most Gulf-region hospitals we visit still operate TIR34-era water infrastructure.
            Here is what we typically upgrade.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                icon: Droplets,
                title: "RO Capacity Uprate",
                text: "Under-sized central RO systems get supplemental skids or replacement to meet ST108 Category 2 endotoxin/TOC limits.",
              },
              {
                icon: ShieldCheck,
                title: "Endotoxin & TOC Monitoring",
                text: "Add in-line conductivity + quarterly endotoxin/TOC testing plus documented staff competency — new under ST108.",
              },
              {
                icon: CheckCircle2,
                title: "Policy & Documentation",
                text: "Rewrite SOPs to reference ST108, add water-quality log templates, and prepare survey binder for Joint Commission/DNV.",
              },
            ].map((f) => (
              <div key={f.title} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <f.icon className="w-6 h-6 text-blue-700 mb-3" />
                <p className="font-bold text-slate-900 mb-1">{f.title}</p>
                <p className="text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-black text-slate-900 mb-8">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <details
              key={i}
              className="group rounded-xl border border-slate-200 bg-white p-5 open:shadow-md transition-shadow"
            >
              <summary className="cursor-pointer flex items-center justify-between font-bold text-slate-900">
                {f.q}
                <ArrowRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform" />
              </summary>
              <p className="mt-3 text-slate-700 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Warning banner */}
      <section className="bg-amber-50 border-y border-amber-200">
        <div className="max-w-4xl mx-auto px-6 py-8 flex items-start gap-4">
          <AlertTriangle className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-amber-900 mb-1">
              TIR34 references in your policies are a Joint Commission audit red flag.
            </p>
            <p className="text-sm text-slate-700 leading-relaxed">
              Since 2024, Joint Commission expects facility water policies to reference the
              current standard (ST108). Policies still citing TIR34 as the primary standard
              often trigger EC.02.05.01 findings.{" "}
              <a href="mailto:info@sterilemate.com?subject=TIR34 to ST108 gap assessment" className="font-semibold text-amber-800 underline">
                Request a gap assessment
              </a>{" "}
              — typical turnaround: one business day.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
